import React from 'react';
import { createPortal } from 'react-dom';
import { formatNameForDisplay, getInitials } from '../../utils/nameUtils';
import {
  getDisplayElo,
  getPlayerNameColor,
  getSinglesClubEloRating,
  formatEloForUi,
  buildPlayerEloTooltipText,
  formatSessionGamesCountLabel,
} from '../../utils/playerUtils';
import { getGenderAvatarBg } from '../../utils/genderColors';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';
import Button from '../common/Button';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import { useStatsTooltip, StatsTooltipCaret } from './SessionPlayerPill';

function UpcomingMatchPlayerRow({ player, useSinglesElo, showEloTooltip = true }) {
  const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const {
    showTooltip,
    setShowTooltip,
    tooltipRef,
    floatingRef,
    tooltipStyle,
    placement,
    onAnchorMouseEnter,
    onAnchorKeyboardOpen,
  } = useStatsTooltip({ width: 300, estHeight: 96, gap: 6 });
  const statsTooltipStr = buildPlayerEloTooltipText(player, { useSinglesElo });

  const statsPortal =
    showEloTooltip &&
    showTooltip &&
    createPortal(
      <div
        ref={floatingRef}
        className="relative max-w-xs px-3 py-2 text-sm rounded-lg pointer-events-none border border-border-medium bg-surface-subtle text-text-primary shadow-lg dark:bg-surface-elevated dark:shadow-xl"
        style={tooltipStyle}
      >
        <div className="font-medium whitespace-pre-line">{statsTooltipStr}</div>
        <StatsTooltipCaret placement={placement} />
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={tooltipRef}
        className="flex items-center justify-between gap-2 rounded-md px-2 py-2 transition-colors hover:bg-surface-subtle"
        onMouseEnter={
          showEloTooltip
            ? (e) => {
                onAnchorMouseEnter(e);
                if (canHover) setShowTooltip(true);
              }
            : undefined
        }
        onMouseLeave={showEloTooltip ? () => canHover && setShowTooltip(false) : undefined}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div
            className={`flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full ${getGenderAvatarBg(player.gender)}`}
            title={player.fullName}
          >
            {player.avatar_url ? (
              <img
                src={getPlayerAvatarPublicUrl(player.avatar_url)}
                alt={player.fullName}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-sm font-semibold text-white">{getInitials(player.fullName || player.name)}</span>
            )}
          </div>
          <span
            className={`truncate text-lg font-medium ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded' : ''} ${getPlayerNameColor(player.gender)}`}
            onClick={showEloTooltip ? () => !canHover && setShowTooltip((p) => !p) : undefined}
            onFocus={
              showEloTooltip
                ? () => {
                    onAnchorKeyboardOpen();
                    setShowTooltip(true);
                  }
                : undefined
            }
            onBlur={showEloTooltip ? () => setShowTooltip(false) : undefined}
            tabIndex={showEloTooltip ? 0 : undefined}
            role={showEloTooltip ? 'button' : undefined}
            aria-label={showEloTooltip ? `View ratings for ${player.fullName || player.name}` : undefined}
          >
            {player.name}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-sm text-text-secondary">
          <span className="tabular-nums whitespace-nowrap" title="Games this session">
            {player.gamesDisplay}
          </span>
        </div>
      </div>
      {statsPortal}
    </>
  );
}

function getTeamAvgElo(teamIds, playersById, playersPerSide) {
  const valid = teamIds.filter((id) => playersById[id]);
  if (valid.length === 0) return 0;
  if (playersPerSide === 1 && valid.length === 1) {
    return getSinglesClubEloRating(playersById[valid[0]]);
  }
  if (valid.length === 2) {
    const [p1Id, p2Id] = valid;
    const p1 = playersById[p1Id];
    const p2 = playersById[p2Id];
    const p1Elo =
      p1 && p2 && p1.gender && p2.gender && p1.gender !== p2.gender
        ? p1.mixed_elo_rating || 0
        : p1?.levels_elo_rating || 0;
    const p2Elo =
      p1 && p2 && p1.gender && p2.gender && p1.gender !== p2.gender
        ? p2.mixed_elo_rating || 0
        : p2?.levels_elo_rating || 0;
    return Math.round((p1Elo + p2Elo) / 2);
  }
  return Math.round(
    valid.reduce((sum, id) => sum + (playersById[id]?.levels_elo_rating || 0), 0) / (valid.length || 1)
  );
}

function getPlayerDisplay(match, playerId, playersById, playersPerSide) {
  const player = playersById[playerId];
  if (!player) return null;

  const nameFormat = formatNameForDisplay(player.name, { maxLength: 20, useInitials: false });
  const teammate =
    playersPerSide === 2
      ? match.team1.includes(playerId)
        ? playersById[match.team1.find((id) => id !== playerId)]
        : playersById[match.team2.find((id) => id !== playerId)]
      : null;

  const displayElo = getDisplayElo(player, teammate, playersPerSide === 1);

  return {
    ...player,
    name: nameFormat.display,
    fullName: player.name,
    elo: displayElo,
    gamesDisplay: formatSessionGamesCountLabel(player),
    gender: player.gender,
    teammate,
  };
}

const UpcomingMatchModal = ({
  /** All queued upcoming matches (same order as main page) */
  matches = [],
  playersById = {},
  isOpen,
  onClose,
  playersPerSide = 2,
  showEloTooltip = true,
}) => {
  if (!isOpen || !matches.length) return null;

  const renderPlayerRow = (player) => (
    <UpcomingMatchPlayerRow key={player.id} player={player} useSinglesElo={playersPerSide === 1} showEloTooltip={showEloTooltip} />
  );

  const renderTeamCard = ({ teamNumber, players, avgElo, accentClass, labelClass, emptyBorderClass }) => (
    <div className="rounded-lg border-2 border-border-medium bg-surface p-3 shadow-sm">
      <div className={`mb-2 flex items-center justify-between border-l-2 pl-2 ${accentClass}`}>
        <span className={`font-semibold ${labelClass}`}>Team {teamNumber}</span>
        <span className="text-base font-semibold text-text-primary">{formatEloForUi(avgElo)}</span>
      </div>

      <div className="space-y-1">
        {players.map((player) => renderPlayerRow(player))}
        {Array.from({ length: Math.max(0, playersPerSide - players.length) }).map((_, idx) => (
          <div
            key={`empty-${teamNumber}-${idx}`}
            className={`rounded-md border border-dashed ${emptyBorderClass} px-2 py-2 text-center text-sm text-text-muted`}
          >
            Waiting for player
          </div>
        ))}
      </div>
    </div>
  );

  const renderOneMatch = (match, matchIndex) => {
    const team1Players = match.team1.map((id) => getPlayerDisplay(match, id, playersById, playersPerSide)).filter(Boolean);
    const team2Players = match.team2.map((id) => getPlayerDisplay(match, id, playersById, playersPerSide)).filter(Boolean);
    const team1AvgElo = getTeamAvgElo(match.team1, playersById, playersPerSide);
    const team2AvgElo = getTeamAvgElo(match.team2, playersById, playersPerSide);

    return (
      <div
        key={matchIndex}
        className={matchIndex > 0 ? 'mt-6 border-t border-border-medium pt-6' : undefined}
      >
        <h3 className="mb-3 text-lg font-semibold text-text-primary">
          Next on court {matchIndex + 1}
          {matches.length > 1 ? (
            <span className="ml-2 text-sm font-normal text-text-muted">(Upcoming match #{matchIndex + 1})</span>
          ) : null}
        </h3>
        <div className="mb-5 grid grid-cols-1 gap-3 rounded-xl border-2 border-info/25 bg-surface p-3 shadow-sm dark:border-primary/30 dark:bg-surface-subtle md:grid-cols-2 md:gap-4 md:p-4">
          {renderTeamCard({
            teamNumber: 1,
            players: team1Players,
            avgElo: team1AvgElo,
            accentClass: 'border-team1',
            labelClass: 'text-team1',
            emptyBorderClass: 'border-team1/40',
          })}
          {renderTeamCard({
            teamNumber: 2,
            players: team2Players,
            avgElo: team2AvgElo,
            accentClass: 'border-team2',
            labelClass: 'text-team2',
            emptyBorderClass: 'border-team2/40',
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent className="max-w-3xl overflow-y-auto border-2 border-info/45 bg-gradient-to-b from-info-light/55 to-surface shadow-xl ring-1 ring-info/20 dark:border-primary/50 dark:from-primary/[0.2] dark:to-surface-elevated dark:ring-primary/30">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-text-primary">
            {matches.length > 1 ? 'Upcoming matches' : 'Upcoming match'}
          </h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        {matches.map((match, idx) => renderOneMatch(match, idx))}

        <div className="flex justify-end pt-2">
          <Button onClick={onClose} variant="default">
            Close
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default UpcomingMatchModal;
